import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaArrowRight, FaCheck, FaCircle, FaClock, FaLifeRing, FaMapMarkerAlt, FaUsers, FaMoneyBillWave, FaShieldAlt } from 'react-icons/fa';
import { heroWsbDiverKelpDark } from '../../assets/images/hero/imageAssets';
import Breadcrumbs from '../../components/Breadcrumbs';
import type { BreadcrumbItem } from '../../components/Breadcrumbs';
import SEOHead from '../../components/SEOHead';
import { getServiceSchema, getOgImageUrl, getBreadcrumbSchema, getOgImageDimensions } from '../../utils/seoSchemas';
import type { CharterDestination } from './charterData';
import {
  DetailHero,
  HeroContent,
  HeroBadge,
  HeroH1,
  HeroTagline,
  HeroCta,
  SectionEyebrow,
  SectionTitle,
  ContentSection,
  Container,
  ExperienceBody,
  ExperienceGrid,
  ExperienceMapWrapper,
  ExperienceParagraph,
  SpeciesSection,
  SpeciesGrid,
  SpeciesRow,
  SpeciesName,
  SeasonBadge,
  TripOptionsSection,
  TripStrip,
  TripRow,
  TripName,
  TripDuration,
  TripPrice,
  ChecklistSection,
  ChecklistColumns,
  ChecklistGroup,
  ChecklistHeading,
  ChecklistItem,
  CheckIcon,
  BulletIcon,
  ChecklistText,
  LogisticsSection,
  LogisticsGrid,
  LogisticsItem,
  LogisticsIcon,
  LogisticsInfo,
  LogisticsLabel,
  LogisticsValue,
  UniqueSection,
  UniqueList,
  UniqueItem,
  UniqueIcon,
  UniqueText,
  CtaSection,
  CtaInner,
  CtaTitle,
  CtaSubtext,
  CtaButton,
} from './styles/CharterDetail.styles';

/* ─── Scroll-triggered reveal ─── */
const AnimatedSection = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.7, delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

/* ─── Category labels for the hero badge ─── */
const CATEGORY_LABELS: Record<CharterDestination['category'], string> = {
  spearfishing: 'Spearfishing Charter',
  sportfishing: 'Sportfishing Charter',
  private: 'Private Charter',
};

/* ─── Canonical path builder ─── */
function getCanonicalPath(dest: CharterDestination): string {
  if (dest.category === 'spearfishing') return `/charters/spearfishing/${dest.slug}`;
  if (dest.category === 'sportfishing') return `/charters/sportfishing`;
  return `/charters/private`;
}

/* ─── Breadcrumb builder ─── */
const CATEGORY_BREADCRUMBS: Record<CharterDestination['category'], { label: string; to: string }> = {
  spearfishing: { label: 'Spearfishing', to: '/charters/spearfishing' },
  sportfishing: { label: 'Sportfishing', to: '/charters/sportfishing' },
  private: { label: 'Private Charters', to: '/charters/private' },
};

function buildBreadcrumbs(dest: CharterDestination): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [
    { label: 'Home', to: '/' },
    { label: 'Charters', to: '/charters' },
  ];

  if (dest.category === 'spearfishing') {
    items.push({ label: CATEGORY_BREADCRUMBS.spearfishing.label, to: CATEGORY_BREADCRUMBS.spearfishing.to });
    items.push({ label: dest.title });
  } else {
    items.push({ label: CATEGORY_BREADCRUMBS[dest.category].label });
  }

  return items;
}

function buildBreadcrumbSchemaItems(dest: CharterDestination) {
  const items = [
    { name: 'Home', url: '/' },
    { name: 'Charters', url: '/charters' },
  ];

  if (dest.category === 'spearfishing') {
    items.push({ name: 'Spearfishing', url: '/charters/spearfishing' });
    items.push({ name: dest.title, url: getCanonicalPath(dest) });
  } else {
    items.push({ name: CATEGORY_BREADCRUMBS[dest.category].label, url: getCanonicalPath(dest) });
  }

  return items;
}

/* ═══════════════════════════════════════════
   CharterDetailTemplate
   ═══════════════════════════════════════════ */

interface Props {
  destination: CharterDestination;
}

const CharterDetailTemplate = ({ destination }: Props) => {
  const canonical = `https://fussaki.com${getCanonicalPath(destination)}`;
  const ogDimensions = getOgImageDimensions(destination.heroImage);

  return (
    <>
      {/* ─── SEO ─── */}
      <SEOHead
        title={destination.seo.title}
        description={destination.seo.description}
        canonical={canonical}
        ogImage={getOgImageUrl(destination.heroImage)}
        ogImageWidth={ogDimensions?.width}
        ogImageHeight={ogDimensions?.height}
        jsonLd={[
          getServiceSchema(
            destination.seo.title,
            destination.seo.description,
            getCanonicalPath(destination),
            {
              offers: destination.tripOptions
                .filter((o) => o.price !== 'Contact for pricing')
                .map((o) => ({ name: o.name, price: o.price })),
            },
          ),
          getBreadcrumbSchema(buildBreadcrumbSchemaItems(destination)),
        ]}
      />

      {/* ─── Section 1: Hero ─── */}
      <DetailHero $bgImage={destination.heroImage} role="img" aria-label={`${destination.title} charter hero — ${CATEGORY_LABELS[destination.category]}`}>
        <HeroContent>
          <HeroBadge>{CATEGORY_LABELS[destination.category]}</HeroBadge>
          <HeroH1>{destination.seo.h1}</HeroH1>
          <HeroTagline>{destination.tagline}</HeroTagline>
          <HeroCta to="/book">
            Book This Charter {FaArrowRight({ size: 14 })}
          </HeroCta>
        </HeroContent>
      </DetailHero>

      {/* ─── Breadcrumbs ─── */}
      <Breadcrumbs items={buildBreadcrumbs(destination)} />

      {/* ─── Section 2: The Experience ─── */}
      <ContentSection>
        <Container>
          <AnimatedSection>
            <SectionEyebrow>The Experience</SectionEyebrow>
            <SectionTitle>{destination.title}</SectionTitle>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            {destination.mapImage ? (
              <ExperienceGrid>
                <ExperienceBody>
                  {destination.fullDescription.map((para, i) => (
                    <ExperienceParagraph key={i}>{para}</ExperienceParagraph>
                  ))}
                </ExperienceBody>
                <ExperienceMapWrapper>
                  <img src={destination.mapImage} alt={`${destination.title} map`} loading="lazy" />
                </ExperienceMapWrapper>
              </ExperienceGrid>
            ) : (
              <ExperienceBody>
                {destination.fullDescription.map((para, i) => (
                  <ExperienceParagraph key={i}>{para}</ExperienceParagraph>
                ))}
              </ExperienceBody>
            )}
          </AnimatedSection>
        </Container>
      </ContentSection>

      {/* ─── Section 3: Target Species ─── */}
      <SpeciesSection>
        <Container>
          <AnimatedSection>
            <SectionEyebrow>Target Species</SectionEyebrow>
            <SectionTitle>What We Hunt</SectionTitle>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <SpeciesGrid>
              {destination.species.map((sp) => (
                <SpeciesRow key={sp.name}>
                  <SpeciesName>{sp.name}</SpeciesName>
                  <SeasonBadge>{sp.season}</SeasonBadge>
                </SpeciesRow>
              ))}
            </SpeciesGrid>
          </AnimatedSection>
        </Container>
      </SpeciesSection>

      {/* ─── Section 4: Trip Options ─── */}
      <TripOptionsSection>
        <Container>
          <AnimatedSection>
            <SectionEyebrow>Trip Options</SectionEyebrow>
            <SectionTitle>Choose Your Trip</SectionTitle>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <TripStrip>
              {destination.tripOptions.map((opt) => (
                <TripRow key={opt.name} to="/book">
                  <TripName>{opt.name}</TripName>
                  <TripDuration>{opt.duration}</TripDuration>
                  <TripPrice>{opt.price} {FaArrowRight({ size: 12 })}</TripPrice>
                </TripRow>
              ))}
            </TripStrip>
          </AnimatedSection>
        </Container>
      </TripOptionsSection>

      {/* ─── Section 5: What's Included / What to Bring ─── */}
      <ChecklistSection>
        <Container>
          <AnimatedSection>
            <SectionEyebrow>Before You Go</SectionEyebrow>
            <SectionTitle>What to Know</SectionTitle>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <ChecklistColumns>
              <ChecklistGroup>
                <ChecklistHeading>What&apos;s Included</ChecklistHeading>
                {destination.included.map((item) => (
                  <ChecklistItem key={item}>
                    <CheckIcon>{FaCheck({ size: 12 })}</CheckIcon>
                    <ChecklistText>{item}</ChecklistText>
                  </ChecklistItem>
                ))}
              </ChecklistGroup>
              <ChecklistGroup>
                <ChecklistHeading>What to Bring</ChecklistHeading>
                {destination.toBring.map((item) => (
                  <ChecklistItem key={item}>
                    <BulletIcon>{FaCircle({ size: 5 })}</BulletIcon>
                    <ChecklistText>{item}</ChecklistText>
                  </ChecklistItem>
                ))}
              </ChecklistGroup>
            </ChecklistColumns>
          </AnimatedSection>
        </Container>
      </ChecklistSection>

      {/* ─── Section 6: Trip Logistics ─── */}
      <LogisticsSection>
        <Container>
          <AnimatedSection>
            <SectionEyebrow>Logistics</SectionEyebrow>
            <SectionTitle>Trip Details</SectionTitle>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <LogisticsGrid>
              <LogisticsItem>
                <LogisticsIcon>{FaClock({ size: 18 })}</LogisticsIcon>
                <LogisticsInfo>
                  <LogisticsLabel>Departure Time</LogisticsLabel>
                  <LogisticsValue>{destination.logistics.departureTime}</LogisticsValue>
                </LogisticsInfo>
              </LogisticsItem>
              <LogisticsItem>
                <LogisticsIcon>{FaMapMarkerAlt({ size: 18 })}</LogisticsIcon>
                <LogisticsInfo>
                  <LogisticsLabel>Departure Location</LogisticsLabel>
                  <LogisticsValue>{destination.logistics.departureLocation}</LogisticsValue>
                </LogisticsInfo>
              </LogisticsItem>
              <LogisticsItem>
                <LogisticsIcon>{FaUsers({ size: 18 })}</LogisticsIcon>
                <LogisticsInfo>
                  <LogisticsLabel>Max Guests</LogisticsLabel>
                  <LogisticsValue>{destination.logistics.maxGuests} per trip</LogisticsValue>
                </LogisticsInfo>
              </LogisticsItem>
              <LogisticsItem>
                <LogisticsIcon>{FaMoneyBillWave({ size: 18 })}</LogisticsIcon>
                <LogisticsInfo>
                  <LogisticsLabel>Deposit Required</LogisticsLabel>
                  <LogisticsValue>{destination.logistics.depositRequired}</LogisticsValue>
                </LogisticsInfo>
              </LogisticsItem>
              <LogisticsItem>
                <LogisticsIcon>{FaShieldAlt({ size: 18 })}</LogisticsIcon>
                <LogisticsInfo>
                  <LogisticsLabel>Cancellation Policy</LogisticsLabel>
                  <LogisticsValue>{destination.logistics.cancellationPolicy}</LogisticsValue>
                </LogisticsInfo>
              </LogisticsItem>
              <LogisticsItem>
                <LogisticsIcon>{FaLifeRing({ size: 18 })}</LogisticsIcon>
                <LogisticsInfo>
                  <LogisticsLabel>Safety</LogisticsLabel>
                  <LogisticsValue>O2 kit, first aid, float lines, and safety diver on board every trip</LogisticsValue>
                </LogisticsInfo>
              </LogisticsItem>
            </LogisticsGrid>
          </AnimatedSection>
        </Container>
      </LogisticsSection>

      {/* ─── Section 7: Unique Details ─── */}
      <UniqueSection>
        <Container>
          <AnimatedSection>
            <SectionEyebrow>Why This Trip</SectionEyebrow>
            <SectionTitle>What Makes It Different</SectionTitle>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <UniqueList>
              {destination.uniqueDetails.map((detail) => (
                <UniqueItem key={detail}>
                  <UniqueIcon>{FaCheck({ size: 14 })}</UniqueIcon>
                  <UniqueText>{detail}</UniqueText>
                </UniqueItem>
              ))}
            </UniqueList>
          </AnimatedSection>
        </Container>
      </UniqueSection>

      {/* ─── Section 8: CTA ─── */}
      <CtaSection $bgImage={heroWsbDiverKelpDark} role="img" aria-label="Spearfisher with White Seabass in kelp — book this charter">
        <AnimatedSection>
          <CtaInner>
            <SectionEyebrow style={{ textAlign: 'center' }}>Ready?</SectionEyebrow>
            <CtaTitle>Book This Charter</CtaTitle>
            <CtaSubtext>
              Check live availability, pick your date, and lock in your spot with Captain Christos.
            </CtaSubtext>
            <CtaButton to="/book">
              Check Availability {FaArrowRight({ size: 16 })}
            </CtaButton>
          </CtaInner>
        </AnimatedSection>
      </CtaSection>
    </>
  );
};

export default CharterDetailTemplate;
